import api, { ApiClientError } from './client';

export interface ErrorDiagnostic {
  errorName?: string;
  errorMessage?: string;
  stackTrace?: string;
  componentStack?: string;
  errorId?: string;
  correlationId?: string;
  networkMethod?: string;
  networkPath?: string;
  networkStatus?: number;
  networkErrorType?: string;
  route?: string;
  appVersion?: string;
  userAgent?: string;
}

interface ErrorWithComponentStack {
  error: unknown;
  componentStack?: string;
}

export interface CreateErrorReportRequest extends ErrorDiagnostic {
  description: string;
  reproductionSteps?: string;
}

export type ErrorReportStatus = 'open' | 'investigating' | 'resolved' | 'dismissed';

export interface AdminErrorReport extends Required<Pick<CreateErrorReportRequest, 'description'>> {
  id: string;
  status: ErrorReportStatus;
  reproductionSteps: string | null;
  errorName: string | null;
  errorMessage: string | null;
  stackTrace: string | null;
  componentStack: string | null;
  errorId: string | null;
  correlationId: string | null;
  networkMethod: string | null;
  networkPath: string | null;
  networkStatus: number | null;
  networkErrorType: string | null;
  route: string | null;
  appVersion: string | null;
  userAgent: string | null;
  adminNote: string | null;
  diagnosticsExpiresAt: string;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
  user: { id: string; name: string | null; email: string };
}

export type AdminErrorReportSummary = Omit<AdminErrorReport, 'stackTrace' | 'componentStack'>;

export interface AdminErrorReportsResponse {
  reports: AdminErrorReportSummary[];
  total: number;
  page: number;
  limit: number;
}

export function normalizeError(error: unknown, componentStack?: string): ErrorDiagnostic {
  if (
    typeof error === 'object' && error !== null && 'error' in error
    && ('componentStack' in error || Object.keys(error).length === 1)
  ) {
    const captured = error as ErrorWithComponentStack;
    return normalizeError(captured.error, captured.componentStack);
  }
  const route = window.location.pathname;
  const common = { route, appVersion: import.meta.env.VITE_APP_VERSION || undefined, userAgent: navigator.userAgent };
  if (error instanceof ApiClientError) {
    return {
      ...common,
      errorName: error.name,
      errorMessage: error.error.slice(0, 1000),
      stackTrace: error.stack?.slice(0, 20_000),
      componentStack: componentStack?.slice(0, 10_000),
      errorId: error.errorId,
      correlationId: error.correlationId,
      networkMethod: error.networkMethod,
      networkPath: error.networkPath,
      networkStatus: error.statusCode,
      networkErrorType: error.networkErrorType,
    };
  }
  if (error instanceof Error) {
    return {
      ...common,
      errorName: error.name.slice(0, 100),
      errorMessage: error.message.slice(0, 1000),
      stackTrace: error.stack?.slice(0, 20_000),
      componentStack: componentStack?.slice(0, 10_000),
    };
  }
  return common;
}

export const errorReportsService = {
  // Keep a failed report submission from replacing the incident being reported.
  create: (request: CreateErrorReportRequest) => api.post<{ id: string; createdAt: string }>(
    '/error-reports',
    request,
    { suppressErrorCapture: true },
  ),
  list: (page = 1, limit = 25, status?: ErrorReportStatus) => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (status) params.set('status', status);
    return api.get<AdminErrorReportsResponse>(`/admin/error-reports?${params}`);
  },
  get: (id: string) => api.get<AdminErrorReport>(`/admin/error-reports/${id}`),
  update: (id: string, status: ErrorReportStatus, adminNote?: string) =>
    api.patch<AdminErrorReport>(`/admin/error-reports/${id}`, { status, adminNote }),
};